import { toast } from 'sonner';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import FormHelperText from '@mui/material/FormHelperText';

import { uploadFile } from 'src/utils/upload';

import { Upload, UploadBox, UploadAvatar } from '../upload';

import type { UploadProps } from '../upload';

// ----------------------------------------------------------------------

type Props = UploadProps & {
  name: string;
};

// ----------------------------------------------------------------------

export function RHFUploadAvatar({ name, ...other }: Props) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const onDrop = (acceptedFiles: File[]) => {
          const value = acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        return (
          <div>
            <UploadAvatar value={field.value} error={!!error} onDrop={onDrop} {...other} />

            {!!error && (
              <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                {error.message}
              </FormHelperText>
            )}
          </div>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadBox({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox value={field.value} error={!!error} {...other} />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUpload({ name, multiple, helperText, onUpload, slotProps, ...other }: Props) {
  const [uploading, setUploading] = useState(false);
  const { control, setValue, setError } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const uploadProps = {
          multiple,
          accept: { 'image/*': [] },
          error: !!error,
          helperText: error?.message ?? helperText,
        };

        const handleUpload = () => {
          setUploading(true);

          console.log(field.value, 'field.value');
          // Filter only File objects for upload
          const filesToUpload = Array.isArray(field.value)
            ? field.value.filter((item: File | string) => item instanceof File)
            : field.value instanceof File
              ? field.value
              : [];

          if ((Array.isArray(filesToUpload) && filesToUpload.length === 0) || !filesToUpload) {
            toast.error('Yüklenecek yeni bir dosya bulunamadı. Dosyalar zaten yüklü.');
            setUploading(false);
            return;
          }

          uploadFile(filesToUpload)
            .then((fileNames) => {
              // Merge new URLs with existing URLs
              const newUrls = Array.isArray(fileNames) ? fileNames : [fileNames];
              const updatedValue = Array.isArray(field.value)
                ? [
                    ...field.value.filter((item: File | string) => typeof item === 'string'),
                    ...newUrls,
                  ]
                : newUrls;

              setValue(name, updatedValue, { shouldValidate: true });
              toast.success('Dosyalar başarıyla yüklendi.');
            })
            .catch((err) => {
              toast.error(err.message);
            })
            .finally(() => {
              setUploading(false);
            });

          onUpload?.();
        };

        const onDrop = (acceptedFiles: File[]) => {
          const maxFiles = other?.maxFiles ?? 10;

          const totalFiles = field.value
            ? field.value.length + acceptedFiles.length
            : acceptedFiles.length;

          if (field.value && totalFiles > maxFiles) {
            setError(name, { message: `En fazla ${maxFiles} dosya yükleyebilirsiniz.` });
            return;
          }
          const value = multiple ? [...(field.value || []), ...acceptedFiles] : acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        const onRemove = (file: File | string) => {
          console.log(file, field, 'remove çalıştı');
          const value = field.value.filter((f: File | string) => f !== file);
          setValue(name, value, { shouldValidate: true });
        };

        return (
          <Upload
            {...uploadProps}
            value={field.value}
            onDrop={onDrop}
            onRemove={onRemove}
            onUpload={handleUpload}
            slotProps={{
              uploadBtn: {
                disabled: uploading,
              },
              ...slotProps,
            }}
            {...other}
          />
        );
      }}
    />
  );
}
